import { observer } from 'mobx-react';
import React, { useRef } from 'react';
import classnames from 'classnames';

import { UiControlPropsType } from 'shared/types';
import { useObservable, useOnClickOutside } from 'shared/hooks';
import { toDate } from 'shared/utilities';

import { UiIcon } from '../UiIcon';
import { UiCalendar } from '../UiCalendar';

import './styles.scss';

type PropsType = UiControlPropsType<string | null> & {
    minDate?: string;
    scrollIntoView?: boolean;
    withTime?: boolean;
    closeOnSelect?: boolean;
    dateTimeMode?: 'preserve' | 'nextHour';
    hideTimeInputs?: boolean;
    isReadOnly?: boolean;
};

export const UiDatepicker = observer(function UiDatepicker({
    value,
    minDate,
    name,
    onChange = () => undefined,
    style,
    className,
    scrollIntoView = false,
    withTime,
    closeOnSelect = true,
    dateTimeMode = 'preserve',
    hideTimeInputs = false,
    isReadOnly = false,
}: PropsType) {
    const ref = useRef<HTMLDivElement>(null);
    const refOuter = useRef<HTMLDivElement>(null);
    const store = useObservable({
        isOpened: false,
    });
    const classNames = classnames('ui-new-datepicker', className, {
        'ui-new-datepicker--opened': store.isOpened,
        'ui-new-datepicker--empty': !value,
        'ui-new-datepicker--readonly': isReadOnly,
    });

    const date = toDate(value);

    useOnClickOutside(ref, () => store.set('isOpened', false));

    const handleToggle = () => {
        if (isReadOnly) {
            return;
        }

        store.set('isOpened', !store.isOpened);

        if (scrollIntoView) {
            setTimeout(() => {
                refOuter?.current?.scrollIntoView({
                    behavior: 'smooth',
                });
            }, 100);
        }
    };

    return (
        <div className={classNames} ref={ref} style={style}>
            <div className="ui-new-datepicker-control ui-new-datepicker-control--validation" onClick={handleToggle}>
                <UiIcon.CalendarDays size={24} />
                <span>{!value ? 'Указать дату' : date.format(withTime ? 'DD MMMM YYYY HH:mm' : 'DD MMMM YYYY')}</span>
                <UiIcon.ChevronDown className={'ui-new-datepicker-control__chevron'} size={16} />
            </div>
            {store.isOpened && (
                <div className={'ui-new-datepicker__outer'} ref={refOuter}>
                    <UiCalendar
                        withTime={withTime}
                        dateTimeMode={dateTimeMode}
                        hideTimeInputs={hideTimeInputs}
                        minDate={minDate}
                        name={name}
                        onChange={(data, event) => {
                            onChange(data);
                            if (closeOnSelect && event?.type !== 'datetime') {
                                store.set('isOpened', false);
                            }
                        }}
                        value={value}
                    />
                </div>
            )}
        </div>
    );
});
